import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {InputText} from "./form/InputText";
import {InputTextarea} from "./form/InputTextarea";
import {Button} from "./form/Button";
import {orderCorrespondenceLabels, orderCorrespondenceType} from "../js/constants";
import {get, handleApiError, handleExpiredEntryLinkError, post} from "../js/api";
import {
    corsFetch,
    germanDateTime,
    RefreshSignal,
    useRefreshEffect
} from "../js/util";
import {getOwnUser} from "../controller/Profile";
import {InputFileFancy} from "./form/InputFileFancy";
import {AttachmentList} from "./AttachmentList";

const ATTACHMENT_ACCEPT = "image/*,application/pdf";
const MAX_ATTACHMENT_MB = 10;
const MAX_ATTACHMENT_COUNT = 3;

const createUploadFilename = (file) => {
    const originalName = file.name || "Anhang";
    const dotIndex = originalName.lastIndexOf(".");
    const extension = dotIndex > 0 ? originalName.substring(dotIndex).toLowerCase() : "";

    return `Anhang_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${extension}`;
}

export const uploadCorrespondenceAttachment = async (file, isPublic = false) => {
    const filename = createUploadFilename(file);
    const fileType = file.type || "application/octet-stream";
    const resp = await post(`${isPublic ? "/public" : ""}/s3/generateStagingUpload`, {
        "purpose": "ORDER_CORRESPONDENCE",
        "filename": filename,
        "fileType": fileType
    });

    const doPut = async () => {
        const uploadResponse = await corsFetch(resp.httpUploadUri, {
            method: "PUT",
            body: file,
            headers: {
                "Content-Type": fileType,
                "Content-Disposition": "attachment; filename*=UTF-8''" + encodeURIComponent(filename)
            }
        });

        if (!uploadResponse.ok) {
            throw new Error(`Upload failed with status ${uploadResponse.status}`);
        }
    };

    try {
        await doPut();
    } catch (e) {
        await new Promise(r => setTimeout(r, 1000));
        await doPut();
    }

    return {s3identifier: resp.s3identifier, claimToken: resp.claimToken};
}

export const prepareOrderCorrespondenceMessage = async (message, isPublic = false) => {
    if (!message) {
        return message;
    }

    const attachments = await Promise.all((message.attachments || [])
        .filter(attachment => attachment?.file || attachment?.s3identifier)
        .slice(0, MAX_ATTACHMENT_COUNT)
        .map(async attachment => {
            if (attachment.file) {
                return await uploadCorrespondenceAttachment(attachment.file, isPublic);
            }

            return {s3identifier: attachment.s3identifier, claimToken: attachment.claimToken};
        }));

    return {...message, attachments};
}

const URL_PATTERN = /https:\/\/[^\s<>"']+/gi;

const trimTrailingUrlPunctuation = (url) => {
    let cleanUrl = url;
    let trailing = "";
    while (cleanUrl.length > 0 && ".,;:!?)".includes(cleanUrl.charAt(cleanUrl.length - 1))) {
        trailing = cleanUrl.charAt(cleanUrl.length - 1) + trailing;
        cleanUrl = cleanUrl.substring(0, cleanUrl.length - 1);
    }
    return {cleanUrl, trailing};
}

const FormattedMessageText = ({text, linkify = false}) => {
    const lines = (text || "").split(/\r?\n/);

    return <span>
        {lines.map((line, lineIndex) => {
            if (!linkify) {
                return <Fragment key={lineIndex}>
                    {lineIndex > 0 && <br/>}
                    {line}
                </Fragment>;
            }

            const parts = [];
            let lastIndex = 0;
            for (const match of line.matchAll(URL_PATTERN)) {
                const url = match[0];
                const index = match.index;
                if (index > lastIndex) {
                    parts.push(line.substring(lastIndex, index));
                }

                const {cleanUrl, trailing} = trimTrailingUrlPunctuation(url);
                parts.push(<a key={`${lineIndex}-${index}`} href={cleanUrl} target="_blank" rel="noopener noreferrer">
                    {cleanUrl}
                </a>);
                if (trailing) {
                    parts.push(trailing);
                }
                lastIndex = index + url.length;
            }

            if (lastIndex < line.length) {
                parts.push(line.substring(lastIndex));
            }

            return <Fragment key={lineIndex}>
                {lineIndex > 0 && <br/>}
                {parts}
            </Fragment>;
        })}
    </span>;
}

export const OrderCorrespondence = ({identifier, isPublic = false, adminView = true, onCreateReply = null, onMessagesLoaded = null, refresh = new RefreshSignal()}) => {

    const [messages, setMessages] = useState([]);

    const [reply, setReply] = useState();
    const [attachmentInputSlots, setAttachmentInputSlots] = useState([{id: 0}]);
    const nextAttachmentSlotId = useRef(1);
    const attachmentUniqueRef = useRef(new Set());

    const adminMessages = useMemo(() => [orderCorrespondenceType.information, orderCorrespondenceType.question, orderCorrespondenceType.keycard], []);
    const usrMessages = useMemo(() => [orderCorrespondenceType.user_response], []);
    const messageTemplates = useMemo(() => Reply_Templates.filter(t => (adminView ? adminMessages : usrMessages).includes(t.type)), [adminView, adminMessages, usrMessages]);

    const setCurrentReply = useCallback((nextReply) => {
        setReply(nextReply);
        onCreateReply?.(nextReply);
    }, [onCreateReply])

    const resetAttachmentInputs = useCallback(() => {
        attachmentUniqueRef.current.clear();
        setAttachmentInputSlots([{id: nextAttachmentSlotId.current++}]);
    }, [])

    const selectReply = useCallback((nextReply) => {
        resetAttachmentInputs();
        setCurrentReply(nextReply ? {...nextReply, attachments: nextReply.attachments || []} : nextReply);
    }, [resetAttachmentInputs, setCurrentReply])

    const loadMsgs = useCallback(() => {
        const messagesRequest = !isPublic
            ? get(`/orders/${identifier}/correspondence`, false)
            : get(`/public/orders/${identifier}/correspondence`, false);
        messagesRequest.then(resp => {
            setMessages(resp);
            onMessagesLoaded?.(resp);
        }).catch(err => {
            if (!handleExpiredEntryLinkError(err)) {
                handleApiError(err);
            }
        });

        if(messageTemplates.length === 1){
            selectReply({...messageTemplates[0]})
        }else{
            selectReply(null)
        }
    }, [identifier, isPublic, messageTemplates, onMessagesLoaded, selectReply])

    useEffect(loadMsgs, [loadMsgs]);

    useRefreshEffect(loadMsgs, refresh);

    const setReplyWithUsername = (reply) => {
        getOwnUser().then((user) => {
            const withName = {...reply}
            withName.text = withName.text.replace('LOGGED_IN_USER', `${user.firstName} ${user.lastName}`);
            selectReply(withName);
        }).catch(() => {
            selectReply(reply);
        })
    }


    const showLeftSpacer = (message) => {
        let show = message.type === orderCorrespondenceType.user_response;
        if(adminView){
            show = !show;
        }
        return show;
    }

    const showRightSpacer = (message) => {
        let show = message.type !== orderCorrespondenceType.user_response;
        if(adminView){
            show = !show;
        }
        return show;
    }

    const update = (val, prop) => {
        const cpy = {...reply}
        cpy[prop] = val;
        setCurrentReply(cpy);
    }

    const updateAttachmentFile = (file, slotId) => {
        const existing = reply?.attachments || [];
        const withoutSlot = existing.filter(entry => entry.slotId !== slotId);
        update(file ? [...withoutSlot, {slotId, file}] : withoutSlot, "attachments");
    }

    const addAttachmentSlot = () => {
        if (attachmentInputSlots.length >= MAX_ATTACHMENT_COUNT) {
            return;
        }

        setAttachmentInputSlots([...attachmentInputSlots, {id: nextAttachmentSlotId.current++}]);
    }

    return (<>
        <h2>Kommunikation</h2>

        {messages.length > 0 && <>
            <h3>Verlauf</h3>
            <div className={"correspondence-history"}>
                {messages.length > 0 && messages.map(m => {
                    return <div className={"correspondence-row"} key={m.id}>
                            {showLeftSpacer(m) &&
                            <div className={"correspondence-spacer"}/>}
                            <div className={"correspondence-entry " + (showLeftSpacer(m) ? 'sender-style' : '')}>
                                <div className={"correspondence-info-header"}>
                                    <div className={"mt-1 mb-0"}><small>{orderCorrespondenceLabels.get(m.type)}</small></div>
                                    <div className={"mt-1 mb-0"}><small>{m.createdAt ? germanDateTime(m.createdAt) : ''}</small></div>
                                </div>
                                <h4 className={"mt-0"}>
                                    {m.subject}
                                </h4>
                                <FormattedMessageText text={m.text}
                                                      linkify={m.type !== orderCorrespondenceType.user_response}/>
                                <AttachmentList attachments={m.attachments}
                                                compact
                                                responsiveCollapsible
                                                className="correspondence-attachments"/>
                            </div>
                            {showRightSpacer(m) &&
                                <div className={"correspondence-spacer"}/>}
                        </div>
                })}
            </div>
        </>}
        {messages.length === 0 && <div>
            Keine Nachrichten
        </div>
        }

        {onCreateReply && <div className="no-print">
            <h3>Antwort</h3>
            <div>
                {messageTemplates.map(t => {
                    return <Button key={t.type + t.subject}
                            theme={"blue mr-1 mb-1 " + (reply?.subject === t.subject ? 'active' : '')}
                            width={230} onClick={() => setReplyWithUsername({...t})}>{t.title}</Button>
                })}
            </div>
            { reply &&
            <div className="default">
                <InputText label={"Betreff"} name="subject" value={reply.subject} setter={update} required={true}/>
                <InputTextarea label={"Nachricht"} name="text" value={reply.text} setter={update} rows={Math.min(15, Math.max(5, reply.text.split(/\r?\n/).length))} required={true}/>
                <div className="form-input correspondence-file-input">
                    <h4>Bei Bedarf: Weitere Schlüsselfotos / Dokumente</h4>
                    <div className="correspondence-attachment-inputs">
                        {attachmentInputSlots.map((slot, index) =>
                            <div className="correspondence-attachment-input" key={slot.id}>
                                <InputFileFancy name={`attachment-${slot.id}`}
                                                label={`Datei ${index + 1}`}
                                                setter={(file) => updateAttachmentFile(file, slot.id)}
                                                maxMBSize={MAX_ATTACHMENT_MB}
                                                accept={ATTACHMENT_ACCEPT}
                                                uniqueRef={attachmentUniqueRef}/>
                            </div>)}
                    </div>
                    {attachmentInputSlots.length < MAX_ATTACHMENT_COUNT &&
                        <Button type="button" theme="secondary-button mt-1" width={230} onClick={addAttachmentSlot}>
                            Weitere Datei
                        </Button>}
                </div>
            </div>}
        </div>
        }
    </>)
}

export const Reply_Template_Identification = {
    title: 'Sicherungskarte',
    subject: 'Bitte um Übermittlung von Sicherungskarte oder Berechtigung',
    text: 'Guten Tag,\n' +
        '\n' +
        '\n' +
        '\n' +
        'wir bedanken uns für Ihre Anfrage. Ihr Schlüssel unterliegt noch einem Nachschlüsselschutz. Für die Bestellung benötigen wir daher eine Sicherungskarte (Karte im Scheckkartenformat mit der Schlüsselnummer) oder eine Schlüsselberechtigung (Bestätigung) vom Eigentümer oder der Hausverwaltung.\n' +
        '\n' +
        'Unten können Sie diese ganz bequem im Chat hochladen. \n' +
        '\n' +
        'Ein Nachschlüssel kostet ______________ inkl. 20% USt.\n' +
        '\n' +
        '\n' +
        '\n' +
        'Sichere Grüße\n' +
        '\n' +
        'LOGGED_IN_USER\n' +
        '\n' +
        'vom Keydirect-Nachschlüssel-Team',
    type: orderCorrespondenceType.keycard
}

export const Reply_Template_Question = {
    title: 'Nachfrage',
    subject: 'Wir benötigen noch Informationen zu Ihrer Schlüsselanfrage',
    text: 'Guten Tag,\n' +
        '\n' +
        '\n' +
        '\n' +
        'wir bedanken uns für Ihre Anfrage. Zur erfolgreichen Bearbeitung benötigen wir von Ihnen noch folgende Informationen:\n' +
        '\n' +
        '\n' +
        '\n' +
        'Sichere Grüße\n' +
        '\n' +
        'LOGGED_IN_USER\n' +
        '\n' +
        'vom Keydirect-Nachschlüssel-Team',
    type: orderCorrespondenceType.question
}

export const Reply_Template_Notification = {
    title: 'Information',
    subject: 'Information zu Ihrer Schlüssel-Anfrage',
    text: 'Guten Tag,\n'+
        '\n' +
        '\n' +
        '\n' +
        'wir bedanken uns für Ihre Anfrage. Gerne können Sie mehr als 1 Stk. bestellen. Die Anzahl können Sie hier einfach anpassen. Ab 2 Stk. erhalten Sie dann 5%, ab 3 Stk. 10% Rabatt.\n'+
        '\n'+
        'Im Auftragsfall benötigen wir bitte noch ein besseres (schärferes) Foto im Idealfall auf unserer Scancard (siehe Beilage).\n'+
        '\n'+
        'Wir haben den unversicherten Standardversand um € 7,50 angenommen. Wollen Sie Ihre Schlüssellieferung zum Warenwert versichern, was wir empfehlen, können wir den versicherten Versand um € 15,60 anbieten.\n'+
        '\n'+
        'Für diesen Schlüssel benötigen wir eine Sicherungskarte oder eine Schlüsselbestätigung des Eigentümers oder der Hausverwaltung. Wir haben dafür eigene Formulare, sollten Sie keine dieser Dokumente vorliegen haben. Die geforderten Dokumente können Sie in diesem Chat bequem hochladen und wir kümmern uns um den Rest.\n'+
        '\n'+
        'Nach Bestellung und Bezahlung kann die Lieferung innerhalb von 2-5 Werktagen erfolgen. Teilen Sie uns hier bitte Ihre Dringlichkeit mit.\n'+
        '\n'+
        'Geben Sie uns hier im Chat Ihre Wünsche zu Ihrer Schlüsselanfrage bekannt.' +
        '\n' +
        '\n' +
        '\n' +
        'Sichere Grüße\n' +
        '\n' +
        'LOGGED_IN_USER\n' +
        '\n' +
        'vom Keydirect-Nachschlüssel-Team',
    type: orderCorrespondenceType.information
}

export const Reply_Template_Ablehnung = {
    title: 'Keine Fertigung möglich',
    subject: 'Leider können wir diesen Schlüssel nicht nachmachen',
    text: 'Guten Tag,\n' +
        '\n' +
        '\n' +
        '\n' +
        'wir bedanken uns für Ihre Anfrage. Dieses Mal können wir leider nicht weiterhelfen. \n' +
        '\n' +
        'Versuchen Sie es gerne wieder, wenn Sie andere Schlüssel benötigen. Beziehen Sie sich auf Ihre Anfrage-Nummer, dann erhalten Sie beim nächsten Mal einen Sonderrabatt von 10% auf Ihre Bestellung! \n' +
        '\n' +
        '\n' +
        '\n' +
        'Sichere Grüße\n' +
        '\n' +
        'LOGGED_IN_USER\n' +
        '\n' +
        'vom Keydirect-Nachschlüssel-Team',
    type: orderCorrespondenceType.information
}

export const Reply_Template_Scancard = {
    title: 'Scancard notwendig',
    subject: 'Bitte Fotos mit Scancard hochladen',
    text: 'Guten Tag,\n' +
        '\n' +
        '\n' +
        '\n' +
        'wir bedanken uns für Ihre Anfrage. Ihre Fotos sind leider noch nicht ganz in ausreichender Qualität.\n' +
        '\n' +
        'Drucken Sie Sich bitte die Scancard aus: https://shorturl.at/v1IwB und laden Sie 2 neue Fotos (Vorder- und Rückseite des Schlüssels) über den Chat hoch (siehe unten). \n' +
        '\n' +
        'Der Preis für 1 Stk. liegt bei __________ inkl. 20% Ust.\n' +
        '\n' +
        '\n' +
        '\n' +
        'Sichere Grüße\n' +
        '\n' +
        'LOGGED_IN_USER\n' +
        '\n' +
        'vom Keydirect-Nachschlüssel-Team',
    type: orderCorrespondenceType.question
}


export const Reply_Template_Muster = {
    title: 'Muster',
    subject: 'Nachschlüssel nur nach Muster möglich',
    text: 'Guten Tag,\n' +
        '\n' +
        '\n' +
        '\n' +
        'wir bedanken uns für Ihre Anfrage. Dieser Schlüssel kann nicht anhand des Fotos angefertigt werden.\n' +
        '\n' +
        'Wir können den Schlüssel aber nach Muster anfertigen. Senden Sie uns einen Schlüssel per Post oder Paketdienst und wir senden die neuen Schlüssel versandkostenfrei zurück. Beziehen Sie sich bitte auf Ihre Anfrage Nummer.\n' +
        '\n' +
        'Der Preis für 1 Stk. liegt bei __________ inkl. 20% Ust. \n' +
        '\n' +
        '\n' +
        'Sichere Grüße\n' +
        '\n' +
        'LOGGED_IN_USER\n' +
        '\n' +
        'vom Keydirect-Nachschlüssel-Team',
    type: orderCorrespondenceType.question
}

export const Reply_Template_User_Response = {
    title: 'Antwort',
    subject: 'Antwort',
    text: '',
    type: orderCorrespondenceType.user_response
}


export const Reply_Templates = [Reply_Template_Identification, Reply_Template_Question, Reply_Template_Notification, Reply_Template_Muster, Reply_Template_Scancard, Reply_Template_Ablehnung, Reply_Template_User_Response]
